import React, { useState } from 'react';
import { ArrowUpRight, Check, Megaphone, Mic } from 'lucide-react';
import { Reveal } from '../components/Reveal';

function Chips({ options, value, onToggle, multi }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((o) => {
        const on = multi ? value.includes(o) : value === o;
        return (
          <button type="button" key={o} onClick={() => onToggle(o)}
            className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${on ? 'bg-[#111111] text-white' : 'bg-white text-[#111111] hover:bg-[#e5e5e5]'}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function AdvertiseForm() {
  const [f, setF] = useState({ name: '', company: '', email: '', phone: '', formats: [], budget: 'Let’s discuss', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleFormat = (o) => setF({ ...f, formats: f.formats.includes(o) ? f.formats.filter((x) => x !== o) : [...f.formats, o] });
  const submit = (e) => { e.preventDefault(); setSent(true); setTimeout(() => setSent(false), 4000); };
  const inp = 'mt-1.5 w-full bg-white rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#111111] transition';
  return (
    <form onSubmit={submit} className="bg-[#f4f4f4] rounded-3xl p-8 md:p-10">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="text-sm font-medium">Name</label><input value={f.name} onChange={set('name')} required className={inp} placeholder="Your name" /></div>
        <div><label className="text-sm font-medium">Company / Brand</label><input value={f.company} onChange={set('company')} required className={inp} placeholder="Company" /></div>
        <div><label className="text-sm font-medium">Work email</label><input type="email" value={f.email} onChange={set('email')} required className={inp} placeholder="you@company.com" /></div>
        <div><label className="text-sm font-medium">Phone</label><input value={f.phone} onChange={set('phone')} className={inp} placeholder="Optional" /></div>
      </div>
      <div className="mt-5">
        <label className="text-sm font-medium">Formats you’re interested in</label>
        <Chips multi options={['Digital Display', 'Newsletter', 'Sponsored Article', 'Magazine Ad', 'Event Sponsorship', 'Podcast']} value={f.formats} onToggle={toggleFormat} />
      </div>
      <div className="mt-5">
        <label className="text-sm font-medium">Indicative budget</label>
        <Chips options={['Let’s discuss', '< ₹1L', '₹1L–₹5L', '₹5L+']} value={f.budget} onToggle={(o) => setF({ ...f, budget: o })} />
      </div>
      <div className="mt-5"><label className="text-sm font-medium">Brief</label><textarea value={f.message} onChange={set('message')} rows={4} className={`${inp} resize-none`} placeholder="Tell us about your campaign goals…" /></div>
      <button type="submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#111111] text-white font-medium px-6 py-4 rounded-full hover:bg-[#D01919] transition-colors">
        {sent ? (<><Check size={18} /> Request sent</>) : (<>Request media kit <ArrowUpRight size={18} /></>)}
      </button>
    </form>
  );
}

function InterviewForm() {
  const [f, setF] = useState({ name: '', designation: '', company: '', email: '', format: 'Podcast', topic: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); setSent(true); setTimeout(() => setSent(false), 4000); };
  const inp = 'mt-1.5 w-full bg-white rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#111111] transition';
  return (
    <form onSubmit={submit} className="bg-[#f4f4f4] rounded-3xl p-8 md:p-10">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="text-sm font-medium">Full name</label><input value={f.name} onChange={set('name')} required className={inp} placeholder="Name" /></div>
        <div><label className="text-sm font-medium">Designation</label><input value={f.designation} onChange={set('designation')} required className={inp} placeholder="e.g. CEO" /></div>
        <div><label className="text-sm font-medium">Company</label><input value={f.company} onChange={set('company')} required className={inp} placeholder="Organisation" /></div>
        <div><label className="text-sm font-medium">Email</label><input type="email" value={f.email} onChange={set('email')} required className={inp} placeholder="you@company.com" /></div>
      </div>
      <div className="mt-5">
        <label className="text-sm font-medium">Preferred format</label>
        <Chips options={['Podcast', 'Written Q&A', 'Video Interview', 'Panel']} value={f.format} onToggle={(o) => setF({ ...f, format: o })} />
      </div>
      <div className="mt-5"><label className="text-sm font-medium">Topic / expertise</label><input value={f.topic} onChange={set('topic')} className={inp} placeholder="e.g. Green hydrogen offtake" /></div>
      <div className="mt-5"><label className="text-sm font-medium">Anything else</label><textarea value={f.message} onChange={set('message')} rows={4} className={`${inp} resize-none`} placeholder="Share a short pitch or angle…" /></div>
      <button type="submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#111111] text-white font-medium px-6 py-4 rounded-full hover:bg-[#D01919] transition-colors">
        {sent ? (<><Check size={18} /> Request sent</>) : (<>Submit interview request <ArrowUpRight size={18} /></>)}
      </button>
    </form>
  );
}

export default function Advertise() {
  const [tab, setTab] = useState('advertise');
  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <p className="eyebrow text-[#D01919] mb-4">Brand Integrations</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-4xl">Put your brand in the room that matters.</h1>
            <p className="text-[#666666] text-lg mt-6 max-w-xl">
              Reach 120,000+ energy decision-makers through our magazine, newsletter, events and podcast — or share your expertise through a thought-leadership interview.
            </p>
          </Reveal>

          <div className="flex gap-2 mt-12 mb-10">
            <button onClick={() => setTab('advertise')} className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-colors ${tab === 'advertise' ? 'bg-[#111111] text-white' : 'bg-[#f4f4f4] text-[#111111] hover:bg-[#e5e5e5]'}`}>
              <Megaphone size={17} /> Advertise With Us
            </button>
            <button onClick={() => setTab('interview')} className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-colors ${tab === 'interview' ? 'bg-[#111111] text-white' : 'bg-[#f4f4f4] text-[#111111] hover:bg-[#e5e5e5]'}`}>
              <Mic size={17} /> Thought-Leadership Interview
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              {tab === 'advertise' ? (
                <div>
                  <h2 className="font-display text-3xl md:text-4xl">Advertise With Us</h2>
                  <p className="text-[#666666] mt-3">Display, newsletter, sponsored content, magazine placements, event sponsorships and podcast integrations — tell us your goals and we’ll send a media kit.</p>
                  <ul className="mt-6 space-y-3">
                    {['Premium, brand-safe editorial environment', 'Targeted reach across the energy value chain', 'Multi-channel: web, magazine, newsletter, events, podcast'].map((b) => (
                      <li key={b} className="flex items-start gap-3"><span className="mt-0.5 w-6 h-6 rounded-full bg-[#D01919] text-white flex items-center justify-center shrink-0"><Check size={14} strokeWidth={3} /></span><span>{b}</span></li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <h2 className="font-display text-3xl md:text-4xl">Thought-Leadership Interview</h2>
                  <p className="text-[#666666] mt-3">Share your perspective with our audience through a podcast, written Q&amp;A, video interview or panel appearance.</p>
                  <ul className="mt-6 space-y-3">
                    {['Position yourself as a voice in the transition', 'Distributed across our channels and social', 'Editorially curated, professionally produced'].map((b) => (
                      <li key={b} className="flex items-start gap-3"><span className="mt-0.5 w-6 h-6 rounded-full bg-[#D01919] text-white flex items-center justify-center shrink-0"><Check size={14} strokeWidth={3} /></span><span>{b}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </Reveal>
            <Reveal delay={120}>
              {tab === 'advertise' ? <AdvertiseForm /> : <InterviewForm />}
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
